/**
 * SectorRotation Component
 *
 * Displays all 11 GICS sectors as horizontal diverging bars,
 * sorted by performance (best to worst).
 * Neutral grey bars; the signed % reads gain/loss.
 * Toggle between 1D (daily) and 1M (monthly) performance.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../../services/api';
import InfoTooltip from '../../common/InfoTooltip';

interface SectorData {
  sector: string;
  symbol: string;
  change_percent: number;
}

type Period = '1D' | '1M';

const SectorRotation: React.FC = () => {
  const [period, setPeriod] = useState<Period>('1D');
  const [sectors, setSectors] = useState<SectorData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchSectors = async () => {
      setLoading(true);
      setError(false);
      try {
        const response = await apiService.getSectorPerformance(period);
        if (cancelled) return;
        if (response.status === 'success') {
          setSectors(response.sectors);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to fetch sector performance:', err);
        setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchSectors();
    return () => { cancelled = true; };
  }, [period]);

  const handleToggle = (p: Period) => {
    if (p !== period) {
      setPeriod(p);
    }
  };

  const toggle = (
    <div className="sector-period-toggle">
      <button
        className={`sector-period-btn${period === '1D' ? ' active' : ''}`}
        onClick={() => handleToggle('1D')}
      >
        1D
      </button>
      <button
        className={`sector-period-btn${period === '1M' ? ' active' : ''}`}
        onClick={() => handleToggle('1M')}
      >
        1M
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="market-context-card sector-card">
        <div className="market-context-card-header">
          <div className="market-context-card-title">
            Sector Rotation
            <InfoTooltip text="Shows which sectors are leading or lagging. Toggle between daily (1D) and monthly (1M) performance." />
          </div>
          {toggle}
        </div>
        <div className="market-context-loading">Loading...</div>
      </div>
    );
  }

  if (error || sectors.length === 0) {
    return (
      <div className="market-context-card sector-card">
        <div className="market-context-card-header">
          <div className="market-context-card-title">
            Sector Rotation
            <InfoTooltip text="Shows which sectors are leading or lagging. Toggle between daily (1D) and monthly (1M) performance." />
          </div>
          {toggle}
        </div>
        <div className="market-context-loading">Data unavailable</div>
      </div>
    );
  }

  // Find max absolute change for scaling bars
  const maxAbs = Math.max(...sectors.map(s => Math.abs(s.change_percent)), 0.1);

  return (
    <div className="market-context-card sector-card">
      <div className="market-context-card-header">
        <div className="market-context-card-title">
          Sector Rotation
          <InfoTooltip text="Shows which sectors are leading or lagging. Toggle between daily (1D) and monthly (1M) performance." />
        </div>
        {toggle}
      </div>
      <div className="sector-list">
        {sectors.map(s => {
          const isPositive = s.change_percent >= 0;
          // Bars are neutral grey; only the signed % carries gain/loss colour.
          const valueClass = s.change_percent > 0 ? 'db-gain' : s.change_percent < 0 ? 'db-loss' : 'db-zero';
          const barWidth = Math.min((Math.abs(s.change_percent) / maxAbs) * 100, 100);

          return (
            <div key={s.symbol} className="sector-row">
              <span className="sector-name">{s.sector}</span>
              <div className="sector-bar-container">
                {/* Center divider is implicit - bars grow from left (positive) or right (negative) */}
                <div className="sector-bar-track">
                  {isPositive ? (
                    <div className="sector-bar-half sector-bar-right">
                      <div
                        className="sector-bar-fill"
                        style={{ width: `${barWidth}%` }}
                      />
                    </div>
                  ) : (
                    <div className="sector-bar-half sector-bar-left">
                      <div
                        className="sector-bar-fill"
                        style={{ width: `${barWidth}%` }}
                      />
                    </div>
                  )}
                </div>
              </div>
              <span className={`sector-value ${valueClass}`}>
                {isPositive ? '+' : ''}{s.change_percent.toFixed(1)}%
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SectorRotation;
